import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Grid } from '@material-ui/core';
import { errorCodes, lookupsJson, policyType } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { appConfig, brandConstants } from 'brand-config';
import { Form, useFormikContext, FieldArray } from 'formik';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import useSession from 'core/hooks/use-session';
import Field from 'core/components/form/form.v2';
import AddButton from 'core/components/add-button';
import RemoveButton from 'core/components/remove-button';
import { useStore } from 'core/store';
import { Label } from 'core/components/label';
import withDatePicker from 'core/components/with-date-picker';
import useAvailablePolicies from 'quote/hooks/use-available-policies';
import AddressAutocomplete from 'core/components/address-autocomplete';
import { FormField } from 'core/components/form';
import ValueField from 'core/components/value-field';
import { Collapsible } from '../collapsible';
import { ErrorAlert } from '../quote-errors';
import { applicantCodes, priorAddressCodes, propDetailsCodes, sections } from '../quote-forms/quote-forms.constants';
import useStyles from './applicant-details.styles';

const ADDRESS_LOCKED_TOOLTIP =
  'To create an offer at a different address, please use the "Clear Offer" button at the top of this page to start over.';

const ApplicantDetails = ({
  setShowPriorAddressCallout,
  setShowPropertyDetailsCallout,
  sectionsToShow,
  setSectionsToShow,
  showAllSectionsClicked,
  setShowAllSectionsClicked,
  isAddressLocked
}) => {
  const {
    quote: { getAlerts }
  } = useStore();

  // Common function to check if Prior Address or Property Detail are visible.
  const checkVisibility = (source) => source.every((section) => sectionsToShow.includes(section));

  // Get or set if prior address is visible.
  const [priorAddressVisible, setPriorAddressVisible] = useState(checkVisibility(priorAddressCodes));

  // Get or set if property details is visible.
  const [propDetailsVisible, setPropDetailsVisible] = useState(checkVisibility(propDetailsCodes));

  const classes = useStyles();
  const { setFieldValue, setFieldTouched, values, isSubmitting } = useFormikContext();

  const { isAgency, user, canModifyAffinityAndLeadSource, isInternalSales, canWriteMultipleAffinityCodes } =
    useSession();

  const selectablePolicyTypes = useAvailablePolicies(values.state);

  const hasHome = [policyType.Home, policyType.HABundle].includes(values.policyType);
  const hasCondo = [policyType.Condo, policyType.CABundle].includes(values.policyType);
  const phoneRequired = appConfig?.featureFlags?.phoneRequired ?? false;
  // Marketing consent (calls/SMS) only makes sense when a phone number was collected. See issue #30059.
  // When phone is required a number will always be collected, so never disable the consent in that case.
  const marketingConsentEnabled = phoneRequired || Boolean(values.phone?.trim());
  const applicantDateOfBirthAtNewOffer = appConfig?.featureFlags?.applicantDateOfBirthAtNewOffer ?? false;

  const leadSourceOptions = lookupsJson.leadSources;

  const [showAllSectionsVisible, setShowAllSectionsVisible] = useState(true);

  const alerts = getAlerts(applicantCodes, values.policyType);

  useEffect(() => {
    if (typeof isInternalSales === 'boolean') {
      setShowAllSectionsVisible(isInternalSales);
    }
  }, [isInternalSales]);

  useEffect(() => {
    if (isAgency) {
      setFieldValue('leadSource', 'Agency', false);
      setFieldTouched('leadSource', true);
      if (!values.affinity) {
        setFieldValue('affinity', user.affinityCode, false);
        setFieldTouched('affinity', true);
      }
    }
    setFieldValue('rep', user.username, false);
    setFieldTouched('rep', true);
  }, [user, isAgency, setFieldValue, setFieldTouched, values]);

  const applicantErrors = useRef(null);

  useEffect(() => {
    if (alerts.length && applicantErrors.current && isSubmitting) {
      applicantErrors.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [alerts, isSubmitting]);

  useEffect(() => {
    if (values.isNewConstruction && (hasHome || hasCondo)) {
      setShowPriorAddressCallout(true);
      setShowPropertyDetailsCallout(true);

      const isPriorAddressVisible = checkVisibility(priorAddressCodes);
      const isPropDetailsVisible = checkVisibility(propDetailsCodes);
      let sectionsToShowCloned = [...sectionsToShow];

      // If new construction is checked and Prior address section is hidden, it will be included in SectionsToShow variable.
      if (!isPriorAddressVisible) {
        sectionsToShowCloned = [...sectionsToShowCloned, ...priorAddressCodes];
      }

      // If new construction is checked and Property details section is hidden, it will be included in SectionsToShow variable.
      if (!isPropDetailsVisible) {
        sectionsToShowCloned = [...sectionsToShowCloned, ...propDetailsCodes];
      }

      setPriorAddressVisible(isPriorAddressVisible);
      setPropDetailsVisible(isPropDetailsVisible);
      setSectionsToShow(sectionsToShowCloned);
    }

    if (!values.isNewConstruction) {
      setShowPriorAddressCallout(false);
      setShowPropertyDetailsCallout(false);

      let sectionsToShowCloned = [...sectionsToShow];
      // If new construction is unchecked, Prior address will be hidden and removed for SectionsToShow variable.
      if (!priorAddressVisible) {
        sectionsToShowCloned = sectionsToShowCloned.filter((section) => !priorAddressCodes.includes(section));
      }

      // If new construction is unchecked, Property details will be hidden and removed for SectionsToShow variable.
      if (!propDetailsVisible) {
        sectionsToShowCloned = sectionsToShowCloned.filter((section) => !propDetailsCodes.includes(section));
      }

      setSectionsToShow(sectionsToShowCloned);
    }
  }, [values?.isNewConstruction]);

  useEffect(() => {
    if (values.isNewPurchase && (hasHome || hasCondo)) {
      setShowPriorAddressCallout(true);

      const isPriorAddressVisible = checkVisibility(priorAddressCodes);

      let sectionsToShowCloned = [...sectionsToShow];

      // If new purchase is checked and Prior address section is hidden, it will be included in SectionsToShow variable.
      if (!isPriorAddressVisible) {
        sectionsToShowCloned = [...sectionsToShowCloned, ...priorAddressCodes];
      }

      setPriorAddressVisible(isPriorAddressVisible);
      setSectionsToShow(sectionsToShowCloned);
    }

    if (!values.isNewPurchase) {
      setShowPriorAddressCallout(false);

      let sectionsToShowCloned = [...sectionsToShow];
      // If new purchase is unchecked, Prior address will be hidden and removed for SectionsToShow variable.
      if (!priorAddressVisible) {
        sectionsToShowCloned = sectionsToShowCloned.filter((section) => !priorAddressCodes.includes(section));
      }

      setSectionsToShow(sectionsToShowCloned);
    }
  }, [values?.isNewPurchase]);

  useEffect(() => {
    if ((values.isNewPurchase || values.isNewConstruction) && !hasHome && !hasCondo) {
      setFieldValue('isNewConstruction', false);
      setFieldValue('isNewPurchase', false);
      setFieldTouched('isNewConstruction', true);
      setFieldTouched('isNewPurchase', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.policyType]);

  useEffect(() => {
    if (!marketingConsentEnabled && values.optIntoMarketingCampaigns) {
      setFieldValue('optIntoMarketingCampaigns', false);
      setFieldTouched('optIntoMarketingCampaigns', true);
    }
  }, [marketingConsentEnabled]);

  const showAllSections = useCallback(() => {
    setShowAllSectionsClicked(true);
    setSectionsToShow(sections);
  }, [setSectionsToShow, setShowAllSectionsClicked]);

  return (
    <Form>
      {alerts.length > 0 && <ErrorAlert errors={alerts} ref={applicantErrors} id="error-alert-applicant-details" />}
      <Collapsible initialOpen title="Applicant Details*">
        <Field xs={6} type="string" id="firstName" name="firstName" mode="light" label="First Name*" />
        <Field xs={6} type="string" id="lastName" name="lastName" mode="light" label="Last Name*" />
        {/*
          Show DOB immediately alongside first/last name for brands that collect it at new-offer
          time (applicantDateOfBirthAtNewOffer). Intentionally NOT gated on policy type: farm brands
          start with an empty policyType (multiple farm products), so gating on hasFarm hid DOB until
          an address + policy type were chosen. See issue #32430.
        */}
        {applicantDateOfBirthAtNewOffer && (
          <Field xs={6} type="date" id="dateOfBirth" name="dateOfBirth" mode="light" label="Date of Birth*" />
        )}
        {isAddressLocked ? (
          <Grid item xs={12}>
            <ValueField label="Address" value={values.address} lockedValue={{ text: ADDRESS_LOCKED_TOOLTIP }} />
          </Grid>
        ) : (
          <AddressAutocomplete id="address" name="address" xs={12} mode="light" label="Address*" />
        )}
        {isAddressLocked && !alerts.some((alert) => Number(alert.code) === errorCodes.NEEDAPTUNIT) ? (
          <Grid item xs={12}>
            <ValueField
              label="Floor, unit, building, etc."
              value={values.unit || 'N/A'}
              lockedValue={{ text: ADDRESS_LOCKED_TOOLTIP }}
            />
          </Grid>
        ) : (
          <Field xs={12} type="string" id="unit" name="unit" mode="light" label="Floor, unit, building, etc." />
        )}
        {isAddressLocked ? (
          <Grid item xs={4}>
            <ValueField label="City" value={values.city} lockedValue={{ text: ADDRESS_LOCKED_TOOLTIP }} />
          </Grid>
        ) : (
          <Field xs={4} type="string" id="city" name="city" mode="light" label="City*" />
        )}
        {isAddressLocked ? (
          <Grid item xs={4}>
            <ValueField label="State" value={values.state} lockedValue={{ text: ADDRESS_LOCKED_TOOLTIP }} />
          </Grid>
        ) : (
          <Field xs={4} type="state" id="state" name="state" mode="light" label="State*" />
        )}
        {isAddressLocked ? (
          <Grid item xs={4}>
            <ValueField label="Zip Code" value={values.zip} lockedValue={{ text: ADDRESS_LOCKED_TOOLTIP }} />
          </Grid>
        ) : (
          <Field xs={4} type="string" id="zip" name="zip" mode="light" label="Zip Code*" />
        )}
        <Field
          xs={6}
          type="select"
          id="policyType"
          name="policyType"
          mode="light"
          label={selectablePolicyTypes.length > 1 ? 'Policy Type*' : 'Policy Type'}
          disabled={!values.state}
          options={selectablePolicyTypes}
          fast={false}
        />

        {values.policyType && !isFarmBasedPolicyType(values.policyType) && (
          <Grid item className={classes.noUnitNumber} xs={6}>
            <Field
              type="checkbox"
              id="noUnitNumber"
              name="noUnitNumber"
              mode="light"
              label="Condo does not have an apt/unit number"
              disabled={!hasCondo}
              fast={false}
              item
            />
          </Grid>
        )}
        {values.policyType && !isFarmBasedPolicyType(values.policyType) && (
          <>
            <Grid item className={classes.left} xs={6}>
              <Field
                type="checkbox"
                id="isNewConstruction"
                name="isNewConstruction"
                mode="light"
                label="Home built in the last 6 months?"
                disabled={values.isNewPurchase || (!hasHome && !hasCondo)}
                fast={false}
                item
              />
            </Grid>
            <Grid item className={classes.left} xs={6}>
              <Field
                type="checkbox"
                id="isNewPurchase"
                name="isNewPurchase"
                mode="light"
                label="Home purchased in the last 6 months?"
                disabled={values.isNewConstruction || (!hasHome && !hasCondo)}
                fast={false}
              />
            </Grid>
          </>
        )}
        <Field xs={8} type="string" id="email" name="email" mode="light" label="Email" />
        <Field
          xs={4}
          type="string"
          id="phone"
          name="phone"
          mode="light"
          label={phoneRequired ? 'Phone Number' : 'Phone Number (optional)'}
        />
        {!isAgency && (
          <FormField
            name="optIntoMarketingCampaigns"
            type="checkbox"
            disabled={!marketingConsentEnabled}
            label={
              <>
                By submitting their information, the client agrees to receive marketing calls and/or text messages from
                us according to our
                <a
                  className={classes.link}
                  href={brandConstants.COMMUNICATIONS_CONSENT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {' '}
                  {brandConstants.SHORT_NAME} Communication Consent.{' '}
                </a>
                Message and data rates may apply. Message frequency varies.
              </>
            }
            mode="light"
            topAlignCheckbox
            className={classNames(classes.paddingBottom, classes.left)}
          />
        )}
        <FieldArray
          name="additionalPhoneNumbers"
          render={(arrayHelpers) => (
            <Grid container alignItems="center" spacing={2} justifyContent="flex-start">
              {values.additionalPhoneNumbers?.length !== 0 &&
                values.additionalPhoneNumbers?.map((_, index) => (
                  <Grid container key={`addtl-phone-${index}`} className={classes.secondaryPhoneContainer} spacing={2}>
                    <Field
                      mode="light"
                      name={`additionalPhoneNumbers.${index}.phoneNumber`}
                      id={`additionalPhoneNumbers.${index}.phoneNumber`}
                      label="Secondary Phone"
                      type="string"
                      xs={4}
                    />

                    <Field
                      mode="light"
                      name={`additionalPhoneNumbers.${index}.note`}
                      id={`additionalPhoneNumbers.${index}.note`}
                      label="Description"
                      type="string"
                      xs={7}
                    />
                    <RemoveButton
                      xs={2}
                      mode="big"
                      onClick={() => {
                        arrayHelpers.remove(index);
                        setFieldTouched('additionalPhoneNumbers');
                      }}
                    />
                    <Field
                      mode="light"
                      name={`additionalPhoneNumbers.${index}.canText`}
                      id={`additionalPhoneNumbers.${index}.canText`}
                      label="Does the potential member agree to receiving texts at this number?"
                      type="checkbox"
                      className={classes.textCheckbox}
                    />
                  </Grid>
                ))}
              <AddButton
                type="full"
                mode="xl"
                onClick={() => {
                  arrayHelpers.push({ phoneNumber: '', note: '', canText: false });
                  setFieldTouched('additionalPhoneNumbers');
                }}
                label="Add an additional phone number"
              />
            </Grid>
          )}
        />
        {(canModifyAffinityAndLeadSource || canWriteMultipleAffinityCodes) && (
          <Field
            xs={8}
            type="affinity"
            id="affinity"
            mode="light"
            name="affinity"
            label={isAgency ? 'Producer Code' : 'Affinity Partner'}
          />
        )}
        {canModifyAffinityAndLeadSource && (
          <Field
            xs={4}
            type="select"
            id="leadSource"
            name="leadSource"
            mode="light"
            label="Lead Source"
            options={leadSourceOptions}
          />
        )}
        {showAllSectionsVisible && !showAllSectionsClicked && (
          <Grid item xs={12} className={classes.checkboxContainer}>
            <Label type="action" onClick={showAllSections}>
              Show all fields
            </Label>
          </Grid>
        )}
      </Collapsible>
    </Form>
  );
};

ApplicantDetails.propTypes = {
  setShowPriorAddressCallout: PropTypes.func.isRequired,
  setShowPropertyDetailsCallout: PropTypes.func.isRequired,
  sectionsToShow: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  setSectionsToShow: PropTypes.func.isRequired,
  showAllSectionsClicked: PropTypes.bool.isRequired,
  setShowAllSectionsClicked: PropTypes.func.isRequired,
  isAddressLocked: PropTypes.bool
};

export default withDatePicker(ApplicantDetails);
